
import { Component, Prop, Vue } from 'nuxt-property-decorator'
import { debounceTime, fromEvent, Subscription } from 'rxjs'
import { PlaybackStatus } from './utils/EngineService'
import { WebPlayerLogger } from './utils/WebPlayerLogger'
import { WebPlayer } from '~/api'

export enum MicListenerStatus {
  AUTO,
  MUTED,
  HEADPHONES,
}

/**
 * Handle Text To Speech
 */
@Component
export default class MicListener extends Vue {
  @Prop() platformSettings!: WebPlayer
  @Prop() playStatus!: PlaybackStatus

  PlaybackStatus = PlaybackStatus
  MicListenerStatus = MicListenerStatus

  // Speech Recognition
  acceptSTT = false
  isDetectionSpeech = false
  isListening = false
  speechRecognitionSupported = false
  listener: any = null
  speechResultSub: Subscription | null = null
  sttLoading = false
  status = MicListenerStatus.MUTED
  isChirpEnabled = false

  private logger = new WebPlayerLogger(this.isDebug)
  private chirpAudio: HTMLAudioElement | null = null

  get isDebug() {
    return !!this.$route.query.debug
  }

  initSTT() {
    const SpeechRecognitionClass =
      // @ts-ignore
      window.SpeechRecognition || window.webkitSpeechRecognition
    if (!SpeechRecognitionClass) {
      this.fireAnalyticsEvent('legacy_player:microphone_denied')
      return
    }

    this.fireAnalyticsEvent('legacy_player:microphone_authorized')

    this.speechRecognitionSupported = true

    this.listener = new SpeechRecognitionClass()
    this.listener.lang = this.platformSettings.locale
    this.listener.interimResults = true

    this.logger.log('Listener', 'Init')
    if (this.platformSettings.chirp && this.platformSettings.chirp.length > 0) {
      this.chirpAudio = new Audio(this.platformSettings.chirp)
      this.isChirpEnabled = true
    }

    this.listenForSpeech()

    this.speechResultSub = fromEvent(this.listener, 'result')
      .pipe(debounceTime(1000))
      .subscribe((event: any) => {
        this.sttLoading = true
        this.isDetectionSpeech = false

        const resultIndex = event.results.length - 1
        let input = event.results[resultIndex][0].transcript as string

        // Clean up the input
        input = input.replaceAll('.', '').trim().toLowerCase()
        this.logger.log('Listener', '' + `Result: '${input}'`)

        try {
          if (event.results[resultIndex].isFinal) {
            this.logger.log('Engine', '' + `'${input}'`)

            if (this.chirpAudio && this.isChirpEnabled) {
              const promise = this.chirpAudio.play()
              if (promise !== undefined) {
                promise
                  .then((_) => {})
                  .catch((reason) => {
                    console.error('Chirp audio playback failed:', reason)
                    console.info(
                      'Chirp audio playback failed, disabling chirping.'
                    )
                    this.isChirpEnabled = false
                    // this.$bvModal.show('interaction-modal')
                  })
              }
            }

            this.$emit('onResult', input)
          } else {
            this.logger.log('Listener', 'Incomplete ' + `'${input}'`)
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e)
        } finally {
          this.sttLoading = false
        }
      })

    this.listener.onspeechstart = () => {
      this.isDetectionSpeech = true
      this.logger.log('Listener', 'Speech Detected')
    }

    this.listener.onspeechend = () => {
      this.isDetectionSpeech = false
      this.logger.log('Listener', 'Speech Ended')
      this.listener?.stop()
    }

    this.listener.onstart = () => {
      this.logger.log('Listener', 'Started')
      this.isListening = true
    }

    this.listener.onend = () => {
      this.isListening = false
      if (!this.isDetectionSpeech) {
        this.logger.log('Listener', 'Restart')
        this.listenForSpeech()
      }
    }

    this.listener.onerror = (event: any) => {
      this.fireAnalyticsEvent('legacy_player:microphone_error', {
        error: event.error,
      })
      // eslint-disable-next-line no-console
      console.warn('Listener -> speech recognition error', event)
    }
  }

  listenForSpeech() {
    try {
      if (this.acceptSTT && !this.isListening) {
        this.listener?.start()
      } else {
        this.logger.log('Listener', 'Paused')
      }
    } catch (e) {
      this.fireAnalyticsEvent('legacy_player:stt_error', { e })
      // eslint-disable-next-line no-console
      console.warn(e)
    }
  }

  toggleStatus() {
    if (this.playStatus === PlaybackStatus.LOAD) {
      return
    }
    this.status =
      this.status === MicListenerStatus.MUTED
        ? MicListenerStatus.AUTO
        : this.status === MicListenerStatus.AUTO
        ? MicListenerStatus.HEADPHONES
        : MicListenerStatus.MUTED

    if (
      (this.status === MicListenerStatus.AUTO ||
        this.status === MicListenerStatus.HEADPHONES) &&
      !this.listener
    ) {
      this.initSTT()
    } else if (this.status === MicListenerStatus.MUTED) {
      this.speechResultSub?.unsubscribe()
      this.listener?.stop()
      this.listener = null
    }

    this.$emit('onToggleStatus', this.status)
  }
}
